<div
  class="flex max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-card shadow-xl dark:bg-zinc-900"
  [overlayLoader]="loading()">
  <div
    class="dialog-header sticky top-0 z-10 shrink-0 !m-0 !px-6 !py-4 bg-primary text-white shadow-sm flex items-start justify-between gap-3">
    <div class="flex items-center gap-3 min-w-0">
      <div class="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
        <mat-icon class="icon-size-6 !text-white" [svgIcon]="'heroicons_outline:banknotes'"></mat-icon>
      </div>
      <div class="dialog-title min-w-0">
        <h2 class="!text-white !m-0 text-lg font-bold tracking-tight">Pending invoices</h2>
        <p class="dialog-subtitle !text-white/80 !m-0 text-sm mt-0.5">
          Open balances for your service contract
        </p>
      </div>
    </div>
    <button
      mat-icon-button
      type="button"
      class="!text-white hover:!bg-white/10 shrink-0"
      aria-label="Close"
      (click)="close()">
      <mat-icon class="!text-white" [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
    </button>
  </div>

  <mat-dialog-content class="!m-0 min-h-0 flex-1 overflow-y-auto !px-6 !py-5 min-w-0">
    @if (error()) {
      <div
        class="mb-4 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-200">
        <mat-icon class="icon-size-5 shrink-0" [svgIcon]="'heroicons_outline:exclamation-circle'"></mat-icon>
        <span>{{ error() }}</span>
      </div>
    }

    @if (!loading() && !error()) {
      @if (summary(); as s) {
        <section class="scb-kpi-row mb-5" aria-label="Billing summary">
          <div class="scb-kpi scb-kpi--open">
            <div class="scb-kpi__top">
              <span>Open amount</span>
              <div class="scb-kpi__icon">
                <mat-icon [svgIcon]="'heroicons_outline:banknotes'"></mat-icon>
              </div>
            </div>
            <div class="scb-kpi__val" [matTooltip]="fullPkr(s.openAmount)">
              <span class="scb-kpi__prefix">PKR</span>{{ (s.openAmount ?? 0) | shortNumber }}
            </div>
            <div class="scb-kpi__foot">Total outstanding</div>
          </div>

          <div class="scb-kpi scb-kpi--overdue">
            <div class="scb-kpi__top">
              <span>Overdue amount</span>
              <div class="scb-kpi__icon">
                <mat-icon [svgIcon]="'heroicons_outline:exclamation-triangle'"></mat-icon>
              </div>
            </div>
            <div class="scb-kpi__val" [matTooltip]="fullPkr(s.overdueAmount)">
              <span class="scb-kpi__prefix">PKR</span>{{ (s.overdueAmount ?? 0) | shortNumber }}
            </div>
            <div class="scb-kpi__foot">Past due balance</div>
          </div>

          <div class="scb-kpi scb-kpi--count">
            <div class="scb-kpi__top">
              <span>Open invoices</span>
              <div class="scb-kpi__icon">
                <mat-icon [svgIcon]="'heroicons_outline:document-text'"></mat-icon>
              </div>
            </div>
            <div class="scb-kpi__val">{{ s.openCount ?? 0 }}</div>
            <div class="scb-kpi__foot">Unpaid documents</div>
          </div>

          <div class="scb-kpi scb-kpi--overdue-count">
            <div class="scb-kpi__top">
              <span>Overdue invoices</span>
              <div class="scb-kpi__icon">
                <mat-icon [svgIcon]="'heroicons_outline:clock'"></mat-icon>
              </div>
            </div>
            <div class="scb-kpi__val">{{ s.overdueCount ?? 0 }}</div>
            <div class="scb-kpi__foot">Need attention</div>
          </div>
        </section>
      }

      <section aria-label="Invoices">
        <div class="scb-section-head">
          <mat-icon class="icon-size-5 text-primary" [svgIcon]="'heroicons_outline:document-duplicate'"></mat-icon>
          <h3>Invoices</h3>
        </div>

        @if (!invoices().length) {
          <div class="scb-empty">
            <mat-icon class="text-5xl text-slate-300" [svgIcon]="'heroicons_outline:check-circle'"></mat-icon>
            <div class="text-base font-semibold text-slate-600 dark:text-zinc-300">No pending invoices</div>
            <p class="text-sm text-secondary">You are fully settled for this contract.</p>
          </div>
        } @else {
          <div class="scb-table-wrap">
            <table class="scb-table w-full min-w-[640px]">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Status</th>
                  <th>Due</th>
                  <th class="text-right">Amount</th>
                  <th class="text-right">Remaining</th>
                  <th class="w-16"></th>
                </tr>
              </thead>
              <tbody>
                @for (inv of invoices(); track inv.salesInvoiceId) {
                  <tr [class.scb-table__row--overdue]="!!inv.isOverdue">
                    <td>
                      <div class="font-semibold text-neutral-900 dark:text-white">
                        {{ invoiceTitle(inv) }}
                      </div>
                      <div class="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-secondary">
                        @if (inv.invoiceDate) {
                          <span>{{ inv.invoiceDate | companyDate }}</span>
                        }
                        @if (inv.isOverdue) {
                          <span class="scb-pill scb-pill--danger">Overdue</span>
                        }
                      </div>
                    </td>
                    <td>
                      <span class="scb-pill" [ngClass]="'scb-pill--' + paymentStatusTone(inv)">
                        {{ paymentStatusLabel(inv) }}
                      </span>
                    </td>
                    <td class="text-secondary whitespace-nowrap">
                      {{ (inv.dueDate | companyDate) || '—' }}
                    </td>
                    <td class="text-right tabular-nums cursor-help" [matTooltip]="fullPkr(inv.amount)">
                      <span class="text-secondary text-xs me-0.5">PKR</span>{{ inv.amount ?? 0 | number: '1.2-2' }}
                    </td>
                    <td
                      class="text-right tabular-nums font-semibold cursor-help"
                      [matTooltip]="fullPkr(inv.remainingAmount ?? inv.amount)">
                      <span class="text-secondary text-xs font-normal me-0.5">PKR</span
                      >{{ (inv.remainingAmount ?? inv.amount ?? 0) | number: '1.2-2' }}
                    </td>
                    <td class="text-right">
                      @if (inv.salesInvoiceId) {
                        <button
                          mat-icon-button
                          type="button"
                          [matMenuTriggerFor]="invoiceActionsMenu"
                          aria-label="Actions"
                          (click)="openInvoiceActionsMenu(inv)">
                          <mat-icon class="icon-size-5 text-neutral-400 dark:text-neutral-300">more_vert</mat-icon>
                        </button>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>
    } @else if (loading()) {
      <div class="py-16 text-center text-sm text-secondary">Loading your billing…</div>
    }
  </mat-dialog-content>

  <div class="sticky bottom-0 z-10 shrink-0 bg-card px-6 pb-5 pt-2 dark:bg-zinc-900">
    <mat-dialog-actions align="end" class="!m-0 !p-0 min-h-0 gap-2">
      <button mat-flat-button color="primary" type="button" (click)="close()">Close</button>
    </mat-dialog-actions>
  </div>
</div>

<mat-menu #invoiceActionsMenu="matMenu">
  <button mat-menu-item type="button" (click)="viewSelectedInvoice()">
    <mat-icon class="text-secondary icon-size-4" [svgIcon]="'heroicons_outline:document-text'"></mat-icon>
    <span>View invoice</span>
  </button>
  @if (selectedCanPay()) {
    <button mat-menu-item type="button" (click)="paySelectedInvoice()">
      <mat-icon class="text-secondary icon-size-4" [svgIcon]="'heroicons_outline:banknotes'"></mat-icon>
      <span>Pay invoice</span>
    </button>
  }
  @if (selectedCanSubmitProof()) {
    <button mat-menu-item type="button" (click)="submitProofSelectedInvoice()">
      <mat-icon class="text-secondary icon-size-4" [svgIcon]="'heroicons_outline:paper-clip'"></mat-icon>
      <span>Submit proof</span>
    </button>
  }
</mat-menu>
